import React, { useState, useCallback, useRef, useMemo } from 'react';
import { CrudStyles } from '../../shared/Crud';
import addressService from '../../../services/addressService';
import { maskZipCode } from '../../../utils/masks';
import { isValidZipCode } from '../../../utils/validation';
import AddressNameField from '../../shared/AddressNameField';

const { FormGroup, Input, InputGroup } = CrudStyles;

const emptyAddress = () => ({
    name: '',
    zipCode: '',
    street: '',
    number: '',
    complement: '',
    district: '',
    city: '',
    stateAbbr: '',
});

/**
 * Formulário de endereço controlado (estilo ItemsStep).
 * Estado fica no pai; sem useAddress/AddressForm para não interferir na navegação.
 */
export default function AddressStepForm({ address = {}, onChange, errors = {}, disabled = false }) {
    const [loadingCep, setLoadingCep] = useState(false);
    const addr = useMemo(() => ({ ...emptyAddress(), ...address }), [address]);
    const latestAddrRef = useRef(addr);
    latestAddrRef.current = addr;

    const handleChange = useCallback((field, value) => {
        onChange({ ...latestAddrRef.current, [field]: value });
    }, [onChange]);

    const handleInputChange = useCallback((e) => {
        const { name, value } = e.target;
        if (name === 'zipCode') {
            const masked = maskZipCode(value);
            handleChange('zipCode', masked);
            const clean = masked.replace(/\D/g, '');
            if (clean.length === 8 && isValidZipCode(masked)) {
                setLoadingCep(true);
                addressService.getByZipCode(clean)
                    .then((data) => {
                        const current = latestAddrRef.current;
                        onChange({
                            ...current,
                            zipCode: masked,
                            street: data.street || '',
                            district: data.district || '',
                            city: data.city || '',
                            stateAbbr: (data.state && data.state.abbreviation) || '',
                        });
                    })
                    .catch(() => {})
                    .finally(() => setLoadingCep(false));
            }
            return;
        }
        handleChange(name, value);
    }, [handleChange, onChange]);

    const locked = disabled || loadingCep;

    return (
        <>
            <AddressNameField
                value={addr.name}
                onChange={handleInputChange}
                error={errors.addressName}
            />
            <InputGroup>
                <FormGroup flex={0.6}>
                    <label htmlFor="zipCode">CEP *</label>
                    <Input
                        id="zipCode"
                        name="zipCode"
                        value={addr.zipCode}
                        onChange={handleInputChange}
                        $hasError={!!errors.zipCode}
                        placeholder="00000-000"
                        maxLength={9}
                    />
                    {errors.zipCode && <span className="error">{errors.zipCode}</span>}
                </FormGroup>
                <FormGroup flex={1.4}>
                    <label htmlFor="street">Rua *</label>
                    <Input
                        id="street"
                        name="street"
                        value={addr.street}
                        onChange={handleInputChange}
                        $hasError={!!errors.street}
                        disabled={locked}
                        placeholder="Logradouro"
                    />
                    {errors.street && <span className="error">{errors.street}</span>}
                </FormGroup>
            </InputGroup>

            <InputGroup>
                <FormGroup flex={0.5}>
                    <label htmlFor="number">Número *</label>
                    <Input
                        id="number"
                        name="number"
                        value={addr.number}
                        onChange={handleInputChange}
                        $hasError={!!errors.number}
                        placeholder="Nº"
                    />
                    {errors.number && <span className="error">{errors.number}</span>}
                </FormGroup>
                <FormGroup flex={1.5}>
                    <label htmlFor="complement">Complemento</label>
                    <Input
                        id="complement"
                        name="complement"
                        value={addr.complement}
                        onChange={handleInputChange}
                        placeholder="Apto, bloco..."
                    />
                </FormGroup>
            </InputGroup>

            <InputGroup>
                <FormGroup flex={1.5}>
                    <label htmlFor="district">Bairro *</label>
                    <Input
                        id="district"
                        name="district"
                        value={addr.district}
                        onChange={handleInputChange}
                        $hasError={!!errors.district}
                        disabled={locked}
                        placeholder="Bairro"
                    />
                    {errors.district && <span className="error">{errors.district}</span>}
                </FormGroup>
                <FormGroup flex={1}>
                    <label htmlFor="city">Cidade *</label>
                    <Input
                        id="city"
                        name="city"
                        value={addr.city}
                        onChange={handleInputChange}
                        $hasError={!!errors.city}
                        disabled={locked}
                        placeholder="Cidade"
                    />
                    {errors.city && <span className="error">{errors.city}</span>}
                </FormGroup>
                <FormGroup flex={0.5}>
                    <label htmlFor="stateAbbr">UF *</label>
                    <Input
                        id="stateAbbr"
                        name="stateAbbr"
                        value={addr.stateAbbr}
                        onChange={handleInputChange}
                        $hasError={!!errors.stateAbbr}
                        disabled={locked}
                        placeholder="UF"
                        maxLength={2}
                    />
                    {errors.stateAbbr && <span className="error">{errors.stateAbbr}</span>}
                </FormGroup>
            </InputGroup>
        </>
    );
}
